import React, { useState, useEffect } from 'react';
import useDesignAssets from '../hooks/useDesignAssets';

/**
 * TamaraBanner Component - Robust banner loading with fallback
 * Displays Tamara's LinkedIn banner with error handling and fallback UI
 */
const TamaraBanner = ({ 
  className = '',
  fallbackMode = 'compact', // 'compact', 'full', 'minimal'
  showDownloadButton = true,
  onError = null 
}) => {
  const { 
    loadAsset, 
    getAssetUrl, 
    getDownloadUrl, 
    getAssetStatus, 
    isAssetAvailable, 
    errors 
  } = useDesignAssets();
  
  const [bannerContent, setBannerContent] = useState(null);
  const [showFallback, setShowFallback] = useState(false);
  
  const assetName = 'tamara-banner';
  const status = getAssetStatus(assetName);

  useEffect(() => {
    const loadBanner = async () => {
      try {
        const content = await loadAsset(assetName, {
          enableCache: true,
          timeout: 6000,
          fallbackContent: null
        });
        
        if (content) {
          setBannerContent(content);
          setShowFallback(false);
        } else {
          setShowFallback(true);
        }
      } catch (error) {
        console.error('Error loading Tamara banner:', error);
        setShowFallback(true);
        onError?.(error);
      }
    };

    loadBanner();
  }, [loadAsset, onError]);

  // Fallback UI components
  const FallbackBanner = () => {
    const baseClasses = "bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white rounded-lg overflow-hidden";
    
    if (fallbackMode === 'minimal') {
      return (
        <div className={`${baseClasses} p-4 text-center ${className}`}>
          <div className="flex items-center justify-center space-x-3">
            <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold">TN</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Tamara Navarrete</h3>
              <p className="text-sm text-purple-200">Estratega de Marca</p>
            </div>
          </div>
        </div>
      );
    }

    if (fallbackMode === 'compact') {
      return (
        <div className={`${baseClasses} p-6 ${className}`}>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold">TN</span>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold">Tamara Navarrete</h3>
              <p className="text-purple-200 mt-1">Estratega de M&A y Desarrollo de Negocio</p>
              <p className="text-sm text-gray-300 mt-2">
                Especializada en valoración de marcas y estrategias de crecimiento
              </p>
            </div>
          </div>
          
          {showDownloadButton && (
            <div className="mt-4 text-center">
              <a
                href={getAssetUrl(assetName)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
              >
                Ver Banner Original
              </a>
            </div>
          )}
        </div>
      );
    }

    // Full fallback mode
    return (
      <div className={`${baseClasses} p-8 ${className}`}>
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto">
            <span className="text-3xl font-bold">TN</span>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold">Tamara Navarrete</h2>
            <p className="text-purple-200 text-lg mt-1">Estratega de M&A y Desarrollo de Negocio</p>
          </div>
          
          <div className="bg-black/20 rounded-lg p-4 backdrop-blur-sm">
            <p className="text-sm text-gray-200">
              ✨ Especializada en valoración de marcas<br/>
              📈 Estrategias de crecimiento empresarial<br/>
              🎯 Consultoria personalizada para startups y empresas
            </p>
          </div>

          {showDownloadButton && (
            <div className="flex justify-center space-x-3">
              <a
                href={getAssetUrl(assetName)}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
              >
                Ver Banner
              </a>
              {getDownloadUrl(assetName) && (
                <a
                  href={getDownloadUrl(assetName)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
                >
                  Descargar
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className={`bg-gray-800 rounded-lg p-6 animate-pulse ${className}`}>
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-600 rounded-full"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-600 rounded w-1/2"></div>
            <div className="h-3 bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state or fallback
  if (status === 'error' || showFallback || !bannerContent) {
    return <FallbackBanner />;
  }

  // Success: render iframe with banner content
  return (
    <div className={`relative ${className}`}>
      <iframe
        srcDoc={bannerContent}
        className="w-full h-96 border-0 rounded-lg shadow-lg"
        title="Tamara Navarrete LinkedIn Banner"
        loading="lazy"
        sandbox="allow-same-origin allow-scripts"
        onError={() => {
          console.error('Iframe failed to load banner content');
          setShowFallback(true);
        }}
      />
      
      {/* Optional controls overlay */}
      {showDownloadButton && (
        <div className="absolute bottom-4 right-4 flex space-x-2">
          <a
            href={getAssetUrl(assetName)}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 bg-black/70 text-white text-xs rounded hover:bg-black/90 transition-colors"
            title="Ver en nueva ventana"
          >
            🔗 Ver
          </a>
          {getDownloadUrl(assetName) && (
            <a
              href={getDownloadUrl(assetName)}
              className="px-3 py-1 bg-black/70 text-white text-xs rounded hover:bg-black/90 transition-colors"
              title="Descargar banner"
            >
              ⬇️ Descargar
            </a>
          )}
        </div>
      )}
      
      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
          ✅ Banner Loaded
        </div>
      )}
    </div>
  );
};

export default TamaraBanner;